package com.fantasytenis.shared.domain.tennis.entity

import com.fantasytenis.shared.domain.lineup.entity.Category
import kotlinx.serialization.Serializable

/**
 * Player costs breakdown by category (1-12 for each)
 */
@Serializable
data class PlayerCosts(
    val serve: Int,
    val forehand: Int,
    val backhand: Int,
    val volleys: Int,
    val fighting: Int
) {
    /**
     * Get cost for a specific category
     */
    fun getCostForCategory(category: Category): Int {
        return when (category) {
            Category.SERVE -> serve
            Category.FOREHAND -> forehand
            Category.BACKHAND -> backhand
            Category.VOLLEYS -> volleys
            Category.FIGHTING -> fighting
        }
    }

    /**
     * Total cost across all categories (for display)
     */
    val total: Int
        get() = serve + forehand + backhand + volleys + fighting

    /**
     * Average cost across categories
     */
    val average: Double
        get() = total / 5.0
}

/**
 * Tennis Player Entity
 */
@Serializable
data class TennisPlayerEntity(
    val id: String,
    val externalId: String,
    val name: String,
    val firstName: String? = null,
    val lastName: String? = null,
    val country: String,
    val atpRanking: Int? = null,
    val eloRating: Double = 1500.0,
    val gender: String = "M",
    val costs: PlayerCosts,
    val isActive: Boolean = true,
    val avatarUrl: String? = null,
    // Match context fields (populated when fetching slate players)
    val matchId: String? = null,
    val opponentId: String? = null,
    val opponentName: String? = null,
    val opponentRanking: Int? = null,
    val matchTime: String? = null
) {
    /**
     * Get cost for a specific category
     */
    fun getCostForCategory(category: Category): Int {
        return costs.getCostForCategory(category)
    }

    /**
     * Display name (first name + last name or just name)
     */
    val displayName: String
        get() = if (firstName != null && lastName != null) {
            "$firstName $lastName"
        } else {
            name
        }

    /**
     * Country flag emoji (if country code is valid)
     */
    val countryFlag: String
        get() {
            if (country.length != 3) return ""
            // Convert ISO 3166-1 alpha-3 to alpha-2 would need a mapping
            // For now, return empty
            return ""
        }
}

/**
 * Tournament level enum
 */
enum class TournamentLevel(val displayName: String, val pointsMultiplier: Double) {
    GRAND_SLAM("Grand Slam", 1.5),
    MASTERS_1000("Masters 1000", 1.2),
    ATP_500("ATP 500", 1.1),
    ATP_250("ATP 250", 1.0),
    CHALLENGER("Challenger", 0.8),
    ITF("ITF", 0.6);

    companion object {
        fun fromString(value: String): TournamentLevel? {
            return entries.find { it.name.equals(value, ignoreCase = true) }
        }
    }
}

/**
 * Surface type enum
 */
enum class Surface(val displayName: String) {
    HARD("Hard"),
    CLAY("Clay"),
    GRASS("Grass"),
    CARPET("Carpet");

    companion object {
        fun fromString(value: String): Surface? {
            return entries.find { it.name.equals(value, ignoreCase = true) }
        }
    }
}

/**
 * Tournament Entity
 */
@Serializable
data class TournamentEntity(
    val id: String,
    val externalId: String,
    val name: String,
    val location: String? = null,
    val country: String? = null,
    val level: String,
    val surface: String,
    val gender: String = "M",
    val startDate: String,
    val endDate: String,
    val prizeMoney: Double? = null,
    val bestOf: Int = 3,
    val isActive: Boolean = true
) {
    val levelEnum: TournamentLevel?
        get() = TournamentLevel.fromString(level)

    val surfaceEnum: Surface?
        get() = Surface.fromString(surface)

    /**
     * Check if this tournament uses best of 5 (Grand Slams)
     */
    val isBestOfFive: Boolean
        get() = bestOf == 5
}

/**
 * Match status enum
 */
enum class MatchStatus {
    SCHEDULED,
    IN_PROGRESS,
    COMPLETED,
    CANCELLED,
    WALKOVER,
    RETIRED;

    companion object {
        fun fromString(value: String): MatchStatus? {
            return entries.find { it.name.equals(value, ignoreCase = true) }
        }
    }
}

/**
 * Match round enum
 */
enum class MatchRound(val displayName: String) {
    R128("Round of 128"),
    R64("Round of 64"),
    R32("Round of 32"),
    R16("Round of 16"),
    QF("Quarterfinal"),
    SF("Semifinal"),
    F("Final");

    companion object {
        fun fromString(value: String): MatchRound? {
            return entries.find { it.name.equals(value, ignoreCase = true) }
        }
    }
}

/**
 * Period score for a single set in a match
 */
@Serializable
data class PeriodScoreEntity(
    val homeScore: Int,
    val awayScore: Int,
    val setNumber: Int
)

/**
 * Match Entity
 */
@Serializable
data class MatchEntity(
    val id: String,
    val externalId: String,
    val tournamentId: String,
    val player1Id: String,
    val player2Id: String,
    val player1Name: String? = null,
    val player2Name: String? = null,
    val winnerId: String? = null,
    val status: String,
    val round: String? = null,
    val score: String? = null,
    val setsPlayed: Int = 0,
    val scheduledTime: String,
    val startedAt: String? = null,
    val completedAt: String? = null,
    val court: String? = null,
    val competition: String? = null,
    val periodScores: List<PeriodScoreEntity> = emptyList()
) {
    val statusEnum: MatchStatus?
        get() = MatchStatus.fromString(status)

    val roundEnum: MatchRound?
        get() = round?.let { MatchRound.fromString(it) }

    /**
     * Check if match is live
     */
    val isLive: Boolean
        get() = statusEnum == MatchStatus.IN_PROGRESS

    /**
     * Check if match is completed
     */
    val isCompleted: Boolean
        get() = statusEnum == MatchStatus.COMPLETED

    /**
     * Check if match reached Set 4
     */
    val reachedSetFour: Boolean
        get() = setsPlayed >= 4

    /**
     * Check if match reached Set 5
     */
    val reachedSetFive: Boolean
        get() = setsPlayed >= 5
}

/**
 * Slate (collection of matches for a contest)
 */
@Serializable
data class SlateEntity(
    val id: String,
    val name: String,
    val startTime: String,
    val endTime: String,
    val matches: List<MatchEntity>,
    val players: List<TennisPlayerEntity>
)
